import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import SearchIcon from '@/assets/Icons/components/SearchIcon';

interface DistrictOption {
  _id: string;
  name: string;
  email_suffix: string;
  cdn: string;
}

interface DropdownComponentProps {
  label?: string;
  placeholder?: string;
  options?: DistrictOption[];
  value?: string;
  onChange?: any;
  searchable?: boolean;
}

// ...imports and interfaces stay the same

const DistrictComponent: React.FC<DropdownComponentProps> = ({
  placeholder = 'District',
  options = [],
  value = '',
  onChange,
  searchable = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedValue, setSelectedValue] = useState(value);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        event.target instanceof Node &&
        !(dropdownRef.current as unknown as Node).contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleSelect = (option: DistrictOption) => {
    setSelectedValue(option._id);
    onChange?.(option._id, option.name);
    setIsOpen(false);
    setSearchTerm('');
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) setSearchTerm('');
  };

  const selectedDistrict = options.find((opt) => opt._id === selectedValue);

  return (
    <div className="relative w-fit" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={toggleDropdown}
        className={`relative min-w-[180px] w-full bg-white border-2 ${
          isOpen ? 'border-ds-mc-blue-400' : 'border-gray-200'
        } rounded-md  pl-3 pr-10 py-2 text-left cursor-pointer focus:outline-none sm:text-sm`}
      >
        <span
          className={`block truncate ${!selectedValue ? 'text-gray-400' : 'text-gray-900'}`}
          title={selectedDistrict?.name || ''}
        >
          {selectedDistrict?.name || placeholder}
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <ChevronDown
            className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </span>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute  top-full mt-1 z-50 w-[216px] bg-white shadow-lg max-h-60 rounded-md overflow-y-auto text-base  right-0">
          {/* Search */}
          {searchable && (
            <div className="sticky top-0 z-10 bg-white px-2 py-2 border-b border-gray-200">
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  className="w-full pl-8 pr-3 py-2 border-2 border-ds-gray-100 rounded-md text-xs text-ds-gray-500 focus:outline-none placeholder:text-ds-gray-300"
                  placeholder="Search District"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                  <SearchIcon />
                </div>
              </div>
            </div>
          )}

          {/* Options */}
          <div className="py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option._id}
                  onClick={() => handleSelect(option)}
                  className="cursor-pointer select-none py-2 px-3 hover:bg-ds-mc-blue-100 border-b border-ds-cool-gray-100"
                  title={option.name}
                >
                  <span className="truncate block w-full text-ds-gray-700 text-xs">
                    {option.name}
                  </span>
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500 text-sm">
                No search result found. Please check and try again.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DistrictComponent;
